import { Paginated } from "@feathersjs/feathers/lib";
import { DataStatus, DataType, IPackage, IProgram, Services } from "types";
import { action, makeObservable, observable, runInAction } from "mobx";
import client from "../feathers";
import { Curriculum } from "./";
import { Program } from "./program";
import { routingStore } from "app";

export class Package extends Curriculum<IPackage> implements IPackage {
  year:     number      = 2022;
  programs: Program[]   = [];

  constructor(data: IPackage) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Package, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      programs: observable,
      year:     observable,

      loadPrograms: action,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
  }

  /*******************************************************************************************************************************************
   * Database Access
   ********************************************************************************************************************************************/
  public loadPrograms = async () => {
    try {

      const _programs: Paginated<IProgram> = await client.service(Services.Program).find({
        query: { package_id: this.id }
      });
      
      runInAction(() => {
        this.programs = _programs.data.map(p => Program.create(p));
      });
    } catch (err) {
      console.error("Failed to load package programs", err);
    }
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  public get getProgress(): number {
    return this.progress || 0;
  }
  
  public static get = async (id?: number, catalog?: boolean, company_id?: number): Promise<Package | Package[]> => {
    try {
      if(catalog) {
        const packages: Paginated<IPackage> = await client.service(Services.Catalog).find({query: {
          type: DataType.Package,
          id:   id
        }});
        const mapped_packages = packages.data.map(c => Package.create(c));
        return id ? mapped_packages[0] : mapped_packages;
      } else {
        
        if(id) {
          return Package.create(await client.service(Services.Package).get(id));
        } else {
          routingStore.startLoading();
          const packages: Paginated<IPackage> = await client.service(Services.Package).find();
          routingStore.finishLoading();
          if(!packages.total) return [];
          return packages.data.map(p => Package.create(p));
        }
      }
    } catch (err) {
      console.error("Failed to load Packages", err);
      return null;
    }
  }

  public static create(data: IPackage): Package {
    return new Package(data);
  }

  protected fromData(data: IPackage): void {
    super.fromData(data);
    this.year = data.year;
  }

  public toData(): any {
    return {
      ...super.toData(),
      year: this.year,
    };
  }

  public toPatchData(patch: Partial<IPackage>): any {
    return {
      ...super.toPatchData(patch),
      year: "year" in patch ? this.year: undefined,
    };
  }

  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/
  public get canPublish(): boolean {
    if(!this.programs) return false;
    return !this.programs.some(p => p.status !== DataStatus.Published);
  }

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/

  /**
   * Links a program to a package.
   * This offers the same functionality as program.setPackage
   */
  linkProgram = async (program: Program): Promise<boolean> => {
    try {
      await program.setPackage(this, true);
      runInAction(() => this.programs = [program, ...this.programs]);
      return true;
    } catch(err) {
      console.error("Failed to link program", err);
      return false;
    }
  }

  /**
   * Unlinks a program to a package.
   * This offers the same functionality as program.setPackage
   */
  unlinkProgram = async (program: Program): Promise<boolean> => {
    try {
      await program.setPackage(null, true);
      runInAction(() => this.programs = this.programs.filter(p => p.id !== program.id));
      return true;
    }  catch(err) {
      console.error("Failed to unlink program", err);
      return false;
    }
  }
}
